import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

interface RateLimitState {
  count: number;
  windowStart: number;
}

/**
 * RateLimitInterceptor: Throttles sensitive endpoints (login, refresh)
 * to prevent abuse and thundering-herd scenarios.
 *
 * Applies a token-bucket style rate limit per endpoint pattern.
 */
@Injectable()
export class RateLimitInterceptor implements HttpInterceptor {
  /** Max requests per window for sensitive endpoints. */
  private readonly maxRequests = 5;

  /** Window size in milliseconds (30 seconds). */
  private readonly windowMs = 30_000;

  /** Base delay in ms for backoff when rate-limited. */
  private readonly baseDelayMs = 1_000;

  private stateMap = new Map<string, RateLimitState>();

  /** Endpoint patterns to rate-limit. */
  private readonly sensitivePatterns = ['/auth/login', '/auth/refresh'];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const matched = this.sensitivePatterns.find(p => req.url.includes(p));
    if (!matched) {
      return next.handle(req);
    }

    const now = Date.now();
    const state = this.stateMap.get(matched) ?? { count: 0, windowStart: now };

    // Reset window if expired
    if (now - state.windowStart > this.windowMs) {
      state.count = 0;
      state.windowStart = now;
    }

    state.count++;
    this.stateMap.set(matched, state);

    if (state.count > this.maxRequests) {
      // Apply exponential backoff with proportional jitter to avoid thundering herd
      const excess = state.count - this.maxRequests;
      const baseDelay = this.baseDelayMs * Math.pow(2, excess - 1);
      const jitter = Math.random() * baseDelay * 0.1; // ±10% jitter
      const delay = Math.min(baseDelay + jitter, 10_000); // Cap at 10 seconds
      console.debug(`[RateLimitInterceptor] Rate limiting ${matched}, delaying ${Math.round(delay)}ms`);
      return timer(delay).pipe(switchMap(() => next.handle(req)));
    }

    return next.handle(req);
  }
}
